import React from 'react'
import type {
  CharacterIndexAdaptedIn,
  CharacterIndexAttestation,
  CharacterIndexDraft,
  CharacterIndexDraftContribution,
  CharacterIndexEntry,
  CharacterIndexEntryStatusPhase,
  CharacterIndexVariant,
  CharacterIndexVariantCandidate,
} from '@variorum/character-index'
import type { CharacterCoverageSummary } from '../../lib/characterLayerHelpers'

const PHASE_LABELS: Record<CharacterIndexEntryStatusPhase, string> = {
  candidate_draft: 'Candidate',
  partial_draft: 'Partial draft',
  complete_draft: 'Complete draft',
  accepted: 'Accepted',
}

const PHASE_CLASSES: Record<CharacterIndexEntryStatusPhase, string> = {
  candidate_draft: 'border-amber-300 bg-amber-50 text-amber-800',
  partial_draft: 'border-black/20 bg-gray-100 text-gray-700',
  complete_draft: 'border-black bg-white text-black',
  accepted: 'border-emerald-300 bg-emerald-50 text-emerald-800',
}

const DECISION_CLASSES: Record<CharacterIndexDraft['decision'], string> = {
  pending: 'border-amber-300 bg-amber-50 text-amber-800',
  accepted: 'border-emerald-300 bg-emerald-50 text-emerald-800',
  rejected: 'border-black/20 bg-gray-100 text-gray-600',
}

const COMPLETENESS_CLASSES: Record<CharacterIndexDraft['completeness'], string> = {
  partial: 'border-amber-300 bg-amber-50 text-amber-800',
  complete: 'border-black bg-black text-white',
}

const labelize = (value: string): string => value.replace(/[_-]+/g, ' ')

function InlineBadge({
  children,
  className = 'border-black/15 bg-white text-gray-600',
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <span className={`inline-flex max-w-full items-center border px-1.5 py-0.5 font-mono text-[7.5px] font-bold uppercase tracking-[0.1em] ${className}`}>
      <span className="truncate">{children}</span>
    </span>
  )
}

function EntrySection({
  label,
  count,
  children,
}: {
  label: string
  count?: number
  children: React.ReactNode
}) {
  return (
    <section className="border-t border-black/10 pt-2">
      <div className="mb-1.5 flex items-center justify-between gap-2 font-mono text-[8px] font-bold uppercase tracking-[0.14em] text-gray-500">
        <span>{label}</span>
        {typeof count === 'number' && <span>{count}</span>}
      </div>
      {children}
    </section>
  )
}

function EmptyLine({ children = 'None recorded' }: { children?: React.ReactNode }) {
  return (
    <div className="border border-dashed border-black/20 bg-white px-2 py-1.5 font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-gray-400">
      {children}
    </div>
  )
}

function KeyValueRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[72px_minmax(0,1fr)] gap-2 text-[11px] leading-snug">
      <div className="font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-gray-400">{label}</div>
      <div className="min-w-0 text-gray-700">{value}</div>
    </div>
  )
}

function renderTextList(values: readonly string[], emptyLabel = 'None'): React.ReactNode {
  const cleanValues = values.map(value => value.trim()).filter(Boolean)
  if (cleanValues.length === 0) {
    return <span className="font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-gray-400">{emptyLabel}</span>
  }
  return (
    <span className="flex min-w-0 flex-wrap gap-1">
      {cleanValues.map(value => (
        <InlineBadge key={value}>{value}</InlineBadge>
      ))}
    </span>
  )
}

function AttestationRow({ attestation }: { attestation: CharacterIndexAttestation }) {
  const anchorPreview = attestation.sourceAnchors.slice(0, 2).map(anchor => {
    const span = typeof anchor.localStart === 'number' && typeof anchor.localEnd === 'number'
      ? `:${anchor.localStart}-${anchor.localEnd}`
      : ''
    return `${anchor.witnessId}/${anchor.rowId}${span}`
  }).join(', ')
  return (
    <div className="border border-black/15 bg-white px-2 py-1.5 text-[10.5px] leading-snug text-gray-700">
      <div className="flex min-w-0 items-center justify-between gap-2 font-mono text-[8px] font-bold uppercase tracking-[0.1em] text-gray-500">
        <span className="truncate">{attestation.location.witness || 'witness open'}</span>
        <span className="truncate">{attestation.location.chapter}</span>
      </div>
      <div className="mt-1 min-w-0 truncate">
        {attestation.role || 'Role open'}
      </div>
      {anchorPreview && (
        <div className="mt-1 truncate font-mono text-[8px] text-gray-400">
          {anchorPreview}{attestation.sourceAnchors.length > 2 ? ` +${attestation.sourceAnchors.length - 2}` : ''}
        </div>
      )}
      {attestation.note && (
        <div className="mt-1 text-[10px] text-gray-500">{attestation.note}</div>
      )}
    </div>
  )
}

function VariantCandidateRow({ candidate }: { candidate: CharacterIndexVariantCandidate }) {
  const evidenceLabel = candidate.evidence
    .slice(0, 2)
    .map(evidence => `${evidence.rowId} ${labelize(evidence.alignment)}`)
    .join(', ')
  return (
    <div className="border border-black/15 bg-white px-2 py-1.5 text-[10.5px] leading-snug text-gray-700">
      <div className="flex min-w-0 items-center justify-between gap-2">
        <span className="min-w-0 truncate font-serif font-bold">{candidate.to}</span>
        <InlineBadge>{labelize(candidate.basis)}</InlineBadge>
      </div>
      {evidenceLabel && (
        <div className="mt-1 truncate font-mono text-[8px] text-gray-400">
          {evidenceLabel}{candidate.evidence.length > 2 ? ` +${candidate.evidence.length - 2}` : ''}
        </div>
      )}
    </div>
  )
}

function VariantRow({ variant }: { variant: CharacterIndexVariant }) {
  return (
    <div className="border border-black/15 bg-white px-2 py-1.5 text-[10.5px] leading-snug text-gray-700">
      <div className="font-serif font-bold">{variant.to}</div>
      <div className="mt-1 text-gray-500">{variant.grounds}</div>
    </div>
  )
}

function AdaptedInRow({ adaptedIn }: { adaptedIn: CharacterIndexAdaptedIn }) {
  return (
    <div className="border border-black/15 bg-white px-2 py-1.5 text-[10.5px] leading-snug text-gray-700">
      <div className="flex min-w-0 items-center justify-between gap-2">
        <span className="truncate font-mono text-[8px] font-bold uppercase tracking-[0.1em] text-gray-500">{adaptedIn.in}</span>
        <span className="min-w-0 truncate font-serif font-bold">{adaptedIn.as}</span>
      </div>
      {adaptedIn.note && <div className="mt-1 text-gray-500">{adaptedIn.note}</div>}
    </div>
  )
}

function ContributionRow({ contribution }: { contribution: CharacterIndexDraftContribution }) {
  return (
    <div className="border border-black/10 bg-white px-2 py-1.5">
      <div className="flex min-w-0 items-center justify-between gap-2">
        <span className="min-w-0 truncate font-mono text-[8px] font-bold uppercase tracking-[0.1em] text-gray-500">
          {labelize(contribution.source)}
        </span>
        <InlineBadge className={DECISION_CLASSES[contribution.decision]}>{contribution.decision}</InlineBadge>
      </div>
      <div className="mt-1 flex flex-wrap gap-1">
        {contribution.fields.length > 0
          ? contribution.fields.map(field => <InlineBadge key={field}>{labelize(field)}</InlineBadge>)
          : <InlineBadge>No fields</InlineBadge>}
      </div>
      {contribution.note && <div className="mt-1 text-[10px] leading-snug text-gray-500">{contribution.note}</div>}
    </div>
  )
}

function DraftRow({ draft, activeDraftId }: { draft: CharacterIndexDraft; activeDraftId?: string | null }) {
  const isActive = activeDraftId === draft.draftId
  return (
    <div className={`border px-2 py-1.5 ${isActive ? 'border-black bg-[#fffdfa]' : 'border-black/15 bg-white'}`}>
      <div className="flex min-w-0 items-center justify-between gap-2">
        <div className="min-w-0 truncate font-mono text-[8px] font-bold uppercase tracking-[0.1em] text-gray-500">
          {isActive ? 'Active draft' : 'Draft'} <span className="normal-case tracking-normal text-gray-400">{draft.draftId}</span>
        </div>
        <div className="flex shrink-0 gap-1">
          <InlineBadge className={COMPLETENESS_CLASSES[draft.completeness]}>{draft.completeness}</InlineBadge>
          <InlineBadge className={DECISION_CLASSES[draft.decision]}>{draft.decision}</InlineBadge>
        </div>
      </div>
      {draft.supersedesDraftId && (
        <div className="mt-1 truncate font-mono text-[8px] text-gray-400">
          Supersedes {draft.supersedesDraftId}
        </div>
      )}
      {draft.note && <div className="mt-1 text-[10px] leading-snug text-gray-600">{draft.note}</div>}
      <div className="mt-1.5 space-y-1">
        {draft.contributions.length > 0
          ? draft.contributions.map(contribution => (
            <ContributionRow key={contribution.contributionId} contribution={contribution} />
          ))
          : <EmptyLine>No contributions</EmptyLine>}
      </div>
    </div>
  )
}

export interface CharacterIndexEntryCardProps {
  entry: CharacterIndexEntry
  coverage?: CharacterCoverageSummary
  rowPreview?: string
  isSelected?: boolean
  isExpanded?: boolean
  onToggleSelect?: () => void
  reviewActions?: React.ReactNode
}

export function CharacterIndexEntryCard({
  entry,
  coverage,
  rowPreview,
  isExpanded = true,
  isSelected = false,
  onToggleSelect,
  reviewActions,
}: CharacterIndexEntryCardProps) {
  const phaseLabel = PHASE_LABELS[entry.status.phase] || labelize(entry.status.phase)
  const allWitnessCoverage = Boolean(coverage?.isAllWitnesses)
  return (
    <article
      data-testid={`character-index-entry-card-${entry.id}`}
      className={`border border-black bg-white transition-colors ${isSelected ? 'shadow-[3px_3px_0_#000]' : 'shadow-[1px_1px_0_rgba(0,0,0,0.35)]'}`}
    >
      <button
        type="button"
        onClick={onToggleSelect}
        aria-pressed={isSelected ? 'true' : 'false'}
        className={`block w-full px-3 py-2 text-left transition-colors ${
          isSelected ? 'bg-black text-white' : 'bg-white text-black hover:bg-gray-100'
        }`}
      >
        <div className="flex items-start justify-between gap-2">
          <span className="min-w-0 flex-1 truncate font-serif text-[13px] font-bold">
            {entry.lemma}
          </span>
          <span className="flex shrink-0 flex-wrap justify-end gap-1">
            {entry.kind && entry.kind !== 'person' && (
              <InlineBadge className={isSelected ? 'border-white/40 bg-white/10 text-white' : undefined}>
                {labelize(entry.kind)}
              </InlineBadge>
            )}
            <InlineBadge className={isSelected ? 'border-white/40 bg-white/10 text-white' : PHASE_CLASSES[entry.status.phase]}>
              {phaseLabel}
            </InlineBadge>
            <InlineBadge
              className={isSelected
                ? 'border-white/40 bg-white/10 text-white'
                : allWitnessCoverage
                  ? 'border-black bg-black text-white'
                  : 'border-amber-300 bg-amber-50 text-amber-800'}
            >
              {allWitnessCoverage ? 'All' : 'Partial'}
            </InlineBadge>
          </span>
        </div>
        <div className={`mt-1 flex flex-wrap items-center gap-1.5 font-mono text-[9px] font-semibold uppercase tracking-[0.12em] ${
          isSelected ? 'text-white/65' : 'text-gray-500'
        }`}>
          <span>{coverage?.label || '0 witnesses'}</span>
          <span>{entry.attestations.length} attest.</span>
          <span>{entry.status.drafts.length} draft{entry.status.drafts.length === 1 ? '' : 's'}</span>
        </div>
        {rowPreview && (
          <div className={`mt-1 truncate font-mono text-[9px] ${isSelected ? 'text-white/60' : 'text-gray-400'}`}>
            Rows {rowPreview}{(coverage?.rowIds.length || 0) > 3 ? '...' : ''}
          </div>
        )}
      </button>

      {isExpanded && (
      <div className="space-y-2 px-3 py-2">
        <div className="space-y-1.5">
          <KeyValueRow label="ID" value={<span className="font-mono text-[9px] text-gray-500">{entry.id}</span>} />
          <KeyValueRow label="Names" value={(
            <div className="space-y-1">
              <div className="font-serif text-[12px] font-bold text-black">{entry.names.primary}</div>
              <div>{renderTextList(entry.names.variants, 'No variants')}</div>
              <InlineBadge>{labelize(entry.names.note)}</InlineBadge>
            </div>
          )} />
          <KeyValueRow label="Translit." value={(
            <div className="space-y-1">
              <div className="truncate">{entry.transliteration.primary || <span className="font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-gray-400">None</span>}</div>
              <div>{renderTextList(entry.transliteration.variants, 'No variants')}</div>
            </div>
          )} />
        </div>

        <EntrySection label="Attestations" count={entry.attestations.length}>
          <div className="space-y-1">
            {entry.attestations.length > 0
              ? entry.attestations.slice(0, 3).map((attestation, index) => (
                <AttestationRow key={`${attestation.work}:${index}`} attestation={attestation} />
              ))
              : <EmptyLine />}
            {entry.attestations.length > 3 && (
              <InlineBadge>+{entry.attestations.length - 3} more</InlineBadge>
            )}
          </div>
        </EntrySection>

        <EntrySection label="Variants" count={entry.variant.length + entry.variant_candidates.length}>
          <div className="space-y-1">
            {entry.variant_candidates.map(candidate => (
              <VariantCandidateRow key={`${entry.id}:candidate:${candidate.to}:${candidate.basis}`} candidate={candidate} />
            ))}
            {entry.variant.map(variant => (
              <VariantRow key={`${entry.id}:variant:${variant.to}`} variant={variant} />
            ))}
            {entry.variant.length === 0 && entry.variant_candidates.length === 0 && <EmptyLine />}
          </div>
        </EntrySection>

        <EntrySection label="Adaptations" count={entry.adapted_in.length + entry.adapted_from.length}>
          <div className="space-y-1">
            {entry.adapted_in.map(adaptedIn => (
              <AdaptedInRow key={`${entry.id}:adapted-in:${adaptedIn.in}:${adaptedIn.as}`} adaptedIn={adaptedIn} />
            ))}
            {entry.adapted_from.length > 0 && (
              <KeyValueRow label="From" value={renderTextList(entry.adapted_from)} />
            )}
            {entry.adapted_in.length === 0 && entry.adapted_from.length === 0 && <EmptyLine />}
          </div>
        </EntrySection>

        <EntrySection label="Relations" count={entry.relations.length}>
          <div className="space-y-1">
            {entry.relations.map(relation => (
              <div key={`${entry.id}:relation:${relation.to}:${relation.nature}`} className="border border-black/15 bg-white px-2 py-1.5 text-[10.5px] leading-snug text-gray-700">
                <div className="font-serif font-bold">{relation.to}</div>
                <div className="mt-1 font-mono text-[8px] font-bold uppercase tracking-[0.1em] text-gray-500">{labelize(relation.nature)}</div>
              </div>
            ))}
            {entry.relations.length === 0 && <EmptyLine />}
          </div>
        </EntrySection>

        <EntrySection label="Drafts" count={entry.status.drafts.length}>
          <div className="space-y-1">
            <div className="flex flex-wrap gap-1">
              <InlineBadge className={PHASE_CLASSES[entry.status.phase]}>{phaseLabel}</InlineBadge>
              {entry.status.activeDraftId && <InlineBadge>Active {entry.status.activeDraftId}</InlineBadge>}
            </div>
            {entry.status.drafts.length > 0
              ? entry.status.drafts.map(draft => (
                <DraftRow key={draft.draftId} draft={draft} activeDraftId={entry.status.activeDraftId} />
              ))
              : <EmptyLine>No drafts</EmptyLine>}
          </div>
        </EntrySection>
      </div>
      )}

      {reviewActions && (
        <div className="border-t border-black">
          {reviewActions}
        </div>
      )}
    </article>
  )
}
